/**
 * src/services/profileInterest.ts
 *
 * File-level:
 * - Client-side helper service for expressing interest between users.
 * - Pure browser-safe code (no server imports). Uses fetch() to call backend endpoints.
 *
 * Notes:
 * - Uses centralized API configuration for consistent URL management.
 * - Tries several endpoint path variants (common cases / typos) to maximize compatibility with backend.
 */
import { getApiUrl } from '@/config/api';

/**
 * Standard result shape returned by the helper functions.
 */
export interface InterestResult {
  /** Whether operation succeeded */
  success: boolean;
  /** Inserted id or backend-provided id (if available) */
  id?: number | string | null;
  /** When backend responded 409 (duplicate/pending), conflict will be true */
  conflict?: boolean;
  /** Optional message from backend or error */
  message?: string;
}

/**
 * Get API base to call using centralized configuration.
 */
const API_BASE = getApiUrl();

/**
 * Candidate endpoint paths to try for POST express interest.
 * These are intentionally kept without a leading /api segment since API_BASE usually contains /api.
 * We include multiple variants to be resilient against backend naming differences and typos.
 */
const EXPRESS_ENDPOINT_CANDIDATES = [
  '/ProfleInterest/express',
  '/ProfileInterest/express',
  '/profile-interest/express',
  '/ProfileInterest/express', // redundancy for case-safety
];

/**
 * Candidate endpoint paths to try for status/check (GET).
 */
const STATUS_ENDPOINT_CANDIDATES = [
  '/ProfleInterest/status',
  '/ProfileInterest/status',
  '/profile-interest/status',
  '/ProfleInterest/check',
  '/ProfileInterest/check',
  '/profile-interest/check',
];

/**
 * tryEndpoints
 *
 * @description Try multiple endpoint paths (same method & body) until one returns a non-404 response.
 * If a non-404 is returned, the response (Response) is returned to the caller.
 *
 * @param method HTTP method
 * @param paths Array of endpoint path candidates (each will be prefixed with API_BASE)
 * @param options Fetch options (body, headers etc.)
 */
async function tryEndpoints(method: 'GET' | 'POST', paths: string[], options?: RequestInit): Promise<Response> {
  let lastError: any = null;

  for (const p of paths) {
    const url = `${API_BASE}${p}`;
    try {
      const res = await fetch(url, { method, ...options });
      // If backend returned 404, try next candidate. Otherwise return response.
      if (res.status === 404) {
        // continue to next candidate
        lastError = { status: 404, url };
        continue;
      }
      return res;
    } catch (err) {
      // Network-level error — remember and try next
      lastError = err;
      continue;
    }
  }

  // If none succeeded, throw the last error (or a generic one)
  throw lastError || new Error('All endpoint candidates failed');
}

/**
 * expressInterest
 *
 * @description Express interest from one user to another.
 * Tries multiple endpoint name variants until one responds.
 *
 * @param fromUserId ID of the user expressing interest
 * @param toUserId ID of the target profile
 * @param message Optional message text
 * @returns InterestResult normalised for frontend consumption
 */
export async function expressInterest(fromUserId: string, toUserId: string, message = ''): Promise<InterestResult> {
  if (!fromUserId || !toUserId) {
    return { success: false, message: 'fromUserId and toUserId are required' };
  }

  try {
    const body = JSON.stringify({ fromUserId, toUserId, message });
    const res = await tryEndpoints('POST', EXPRESS_ENDPOINT_CANDIDATES, {
      headers: { 'Content-Type': 'application/json' },
      body,
    });

    if (res.status === 409) {
      // Conflict / duplicate pending
      const json = await safeJson(res);
      return { success: false, conflict: true, message: json?.message || 'Conflict: already exists' };
    }

    if (!res.ok) {
      const json = await safeJson(res);
      return { success: false, message: json?.message || `HTTP ${res.status}` };
    }

    const json = await safeJson(res);
    return { success: true, id: (json && (json.id ?? json.ID)) ?? null, message: json?.message };
  } catch (err: any) {
    return { success: false, message: String(err?.message || err) };
  }
}

/**
 * checkExistingInterest
 *
 * @description Checks whether an interest record exists between two users (used to lock UI).
 * Tries multiple GET endpoint variants.
 *
 * @param fromUserId viewer / requester id
 * @param toUserId profile id
 * @returns boolean | object — returns raw object when backend provides details, otherwise false
 */
export async function checkExistingInterest(fromUserId: string, toUserId: string): Promise<boolean | any> {
  if (!fromUserId || !toUserId) return false;

  // Append query params
  const pathsWithQuery = STATUS_ENDPOINT_CANDIDATES.map(p => `${p}?fromUserId=${encodeURIComponent(fromUserId)}&toUserId=${encodeURIComponent(toUserId)}`);

  try {
    const res = await tryEndpoints('GET', pathsWithQuery);
    if (!res.ok) {
      // treat non-OK (but non-404) as no existing interest
      return false;
    }
    const json = await safeJson(res);
    // Some backends return { success: true, exists: true } etc. Normalize:
    if (json && typeof json === 'object') {
      if ('exists' in json) return json.exists;
      if ('success' in json && json.success === true && (json.data || json.record)) return json.data || json.record;
      // fallback: return the full json so caller can inspect
      return json;
    }
    return false;
  } catch (err) {
    return false;
  }
}

/**
 * safeJson
 *
 * @description Helper to parse JSON responses safely.
 * If parsing fails, returns null.
 *
 * @param res Fetch Response
 */
async function safeJson(res: Response): Promise<any | null> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}
